import { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { PencilIcon } from '@heroicons/react/24/outline';
import PageHeader from '../components/layout/PageHeader';
import { useDeletePrompt, usePrompt, useUpdatePrompt, usePromptAgents } from '../hooks/usePrompts';
import DeleteResourceControl from '../components/shared/DeleteResourceControl';
import { PromptForm } from '../components/prompts/PromptForm';
import { ResourceVersionControls } from '../components/versioning/ResourceVersionControls';

export default function PromptDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: prompt, isLoading, error } = usePrompt(id);
  // Delete lives in the header action row beside Edit (task 864). The Prompt
  // page had no delete at all — the only one was the trash icon on the Prompts
  // list, which is not where a User who has opened a prompt is standing.
  const deletePrompt = useDeletePrompt();
  const { data: agentPrompts, isLoading: agentsLoading, isError: agentsError } = usePromptAgents(id);
  const updatePrompt = useUpdatePrompt();
  const [isEditing, setIsEditing] = useState(false);

  const handleSubmit = async (data: any) => {
    await updatePrompt.mutateAsync({ id: id!, data });
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500 dark:text-gray-400">Loading prompt...</div>
      </div>
    );
  }

  if (error || !prompt) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500 dark:text-red-400">Error loading prompt. Please try again.</div>
      </div>
    );
  }

  const actions = (
    <div className="flex items-center gap-2">
      {!isEditing && (
        <button
          onClick={() => setIsEditing(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
          Edit
        </button>
      )}
      <DeleteResourceControl
        flag="innovate.fe.prompts.delete"
        entity="prompts"
        entityLabel="prompt"
        name={prompt.name}
        id={prompt.id}
        variant="button"
        body={<p>Agents using it stop using it; nothing else about them changes.</p>}
        onDelete={() => deletePrompt.mutateAsync(prompt.id)}
        onDeleted={() => navigate('/prompts')}
      />
    </div>
  );

  return (
    <div>
      <PageHeader
        title={prompt.name}
        description={`Prompt #${prompt.id}`}
        actions={actions}
      />

      {/* Version status + Publish (task 725) — read-only indicator below the
          header, never mixed into the action row. */}
      <ResourceVersionControls
        entityType="prompt"
        entityId={prompt.id}
        resourceName={prompt.name}
        className="mt-3"
      />

      {isEditing ? (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <PromptForm
            mode="edit"
            initialData={prompt}
            onSubmit={handleSubmit}
            onCancel={() => setIsEditing(false)}
          />
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Name Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Name</h2>
              <code className="text-sm bg-indigo-50 dark:bg-indigo-900 px-2 py-1 rounded text-indigo-700 dark:text-indigo-300">
                {prompt.name}
              </code>
              <div className="mt-2 flex items-center space-x-2">
                {prompt.isActive === 'true' && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                    Active
                  </span>
                )}
              </div>
            </div>

            {/* Content Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Content</h2>
              <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-x-auto">
                <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
                  {prompt.content}
                </pre>
              </div>
            </div>

            {/* Metadata Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Metadata</h2>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {new Date(prompt.createdAt).toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {new Date(prompt.updatedAt).toLocaleString()}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Langfuse Synced At</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                    {prompt.syncedAt ? new Date(prompt.syncedAt).toLocaleString() : (
                      <span className="text-gray-400 dark:text-gray-500">Never synced</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Sidebar - Right Side */}
          <div className="lg:col-span-1 space-y-6">
            {/* Used by Agents Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                Used by Agents {!agentsLoading && !agentsError && agentPrompts && agentPrompts.length > 0 && (
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({agentPrompts.filter(ap => ap.isActive && !ap.deletedAt).length})
                  </span>
                )}
              </h2>
              {agentsLoading ? (
                <div className="text-sm text-gray-500 dark:text-gray-400">Loading agents...</div>
              ) : agentsError ? (
                <div className="text-sm text-red-500 dark:text-red-400">
                  Couldn&apos;t load agent usage
                </div>
              ) : agentPrompts && agentPrompts.length > 0 ? (
                <ul className="space-y-2">
                  {agentPrompts
                    .filter(ap => ap.isActive && !ap.deletedAt)
                    .map(agentPrompt => (
                      <li key={agentPrompt.id}>
                        <Link
                          to={`/agents/${agentPrompt.agentId}`}
                          className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 flex items-center"
                        >
                          <span className="mr-2">•</span>
                          {agentPrompt.agent?.name || agentPrompt.agentId}
                        </Link>
                      </li>
                    ))}
                </ul>
              ) : (
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  No agents using this prompt
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
